import type { CategoryExecutor } from './executor.types';

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;
const AADHAAR_RE = /^\d{12}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^[6-9]\d{9}$/;

export const executeValidationNode: CategoryExecutor = (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};
  const field = typeof config['field'] === 'string' ? config['field'] : undefined;
  const errorMessage =
    typeof config['errorMessage'] === 'string' ? config['errorMessage'] : 'Validation failed';
  const value = field ? context.payload[field] : undefined;

  if (type.endsWith('.phone-validation')) {
    // OTP pause is handled by the walker via isPauseNode; if we somehow execute, pass.
    return { status: 'pause' };
  }

  if (type.endsWith('.required-validation')) {
    if (value === undefined || value === null || value === '') {
      return { status: 'failure', message: errorMessage, code: 'REQUIRED' };
    }
    return { status: 'success' };
  }

  if (type.endsWith('.regex-validation')) {
    const pattern = typeof config['pattern'] === 'string' ? config['pattern'] : undefined;
    if (pattern && typeof value === 'string' && !new RegExp(pattern).test(value)) {
      return { status: 'failure', message: errorMessage, code: 'REGEX' };
    }
    return { status: 'success' };
  }

  if (type.endsWith('.range-validation')) {
    const n = Number(value);
    const min = typeof config['min'] === 'number' ? config['min'] : undefined;
    const max = typeof config['max'] === 'number' ? config['max'] : undefined;
    if (min !== undefined && n < min) return { status: 'failure', message: errorMessage, code: 'RANGE' };
    if (max !== undefined && n > max) return { status: 'failure', message: errorMessage, code: 'RANGE' };
    return { status: 'success' };
  }

  if (type.endsWith('.cross-field-validation')) {
    const compareField =
      typeof config['compareField'] === 'string' ? config['compareField'] : undefined;
    const op = typeof config['operator'] === 'string' ? config['operator'] : '==';
    const other = compareField ? context.payload[compareField] : undefined;
    const ok = compare(value, other, op);
    return ok
      ? { status: 'success' }
      : { status: 'failure', message: errorMessage, code: 'CROSS_FIELD' };
  }

  if (type.endsWith('.pan-validation')) {
    if (typeof value === 'string' && !PAN_RE.test(value)) {
      return { status: 'failure', message: errorMessage, code: 'PAN' };
    }
    return { status: 'success' };
  }

  if (type.endsWith('.aadhar-validation') || type.endsWith('.aadhaar-validation')) {
    if (typeof value === 'string' && !AADHAAR_RE.test(value.replace(/\s/g, ''))) {
      return { status: 'failure', message: errorMessage, code: 'AADHAAR' };
    }
    return { status: 'success' };
  }

  if (type.endsWith('.email-validation')) {
    if (typeof value === 'string' && !EMAIL_RE.test(value)) {
      return { status: 'failure', message: errorMessage, code: 'EMAIL' };
    }
    return { status: 'success' };
  }

  if (type.includes('phone') && !type.endsWith('.phone-validation')) {
    if (typeof value === 'string' && !MOBILE_RE.test(value)) {
      return { status: 'failure', message: errorMessage, code: 'PHONE' };
    }
    return { status: 'success' };
  }

  if (type.endsWith('.formula-validation') || type.endsWith('.if-validation')) {
    // formula uses same field/errorMessage; optional custom via no extra keys
    return { status: 'success' };
  }

  if (type.endsWith('.custom-validation') && typeof config['script'] === 'string') {
    try {
      const result = context.expressions.evaluate(config['script'], context.payload);
      if (!result) return { status: 'failure', message: errorMessage, code: 'CUSTOM' };
    } catch (error) {
      return {
        status: 'failure',
        message: error instanceof Error ? error.message : errorMessage,
        code: 'CUSTOM',
      };
    }
  }

  return { status: 'success' };
};

function compare(a: unknown, b: unknown, op: string): boolean {
  switch (op) {
    case '==':
      return a === b;
    case '!=':
      return a !== b;
    case '>':
      return Number(a) > Number(b);
    case '<':
      return Number(a) < Number(b);
    case '>=':
      return Number(a) >= Number(b);
    case '<=':
      return Number(a) <= Number(b);
    default:
      return a === b;
  }
}
